import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Copy } from 'lucide-react';
import {
  Department,
  departmentLabelKey,
  intervalTypeLabelKey,
  intervalTypeSchema,
} from '@nbh/shared';
import { adminApi, type AdminPpmSchedule, type ChecklistTemplateOption } from '../../lib/adminApi';
import { api, type LocationOption } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { RetireDialog, RetiredBadge } from './RetireDialog';

/** Recurring preventive maintenance (SPEC.md §5.4). */
export function PpmScheduleManager() {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState<AdminPpmSchedule[] | null>(null);
  const [templates, setTemplates] = useState<ChecklistTemplateOption[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [retiring, setRetiring] = useState<AdminPpmSchedule | null>(null);
  const [prefill, setPrefill] = useState<AdminPpmSchedule | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    // The third call is the unguarded picker endpoint, not the admin tree. This screen
    // only ever wanted a flat, indented list — it flattened the tree on arrival — and the
    // admin tree is moving to SUPER_ADMIN, which a duty manager scheduling maintenance is
    // not. Reading it here would 403 and reject this whole Promise.all, blanking the
    // Maintenance tab for every ADMIN with an error about a fetch they never asked for.
    Promise.all([adminApi.ppm.list(), adminApi.ppm.templates(), api.listLocations()])
      .then(([list, templateOptions, places]) => {
        setSchedules(list);
        setTemplates(templateOptions);
        setLocations(indentPlaces(places));
      })
      .catch((caught: Error) => setError(caught.message));
  }, []);

  useEffect(load, [load]);

  if (error && !schedules)
    return (
      <p role="alert" className="text-sm text-red-400">
        {error}
      </p>
    );
  if (!schedules) return <p className="text-sm text-slate-400">{t('admin.loading')}</p>;

  return (
    <div className="space-y-4">
      {templates.length === 0 ? (
        // Without a template a schedule has no steps to instantiate, so creation is
        // pointless rather than merely empty.
        <p className="rounded-lg border border-amber-700/50 bg-amber-950/30 p-3 text-xs text-amber-200">
          {t('adminPpm.needsTemplate')}
        </p>
      ) : (
        <CreateScheduleForm
          templates={templates}
          locations={locations}
          prefill={prefill}
          onClearPrefill={() => setPrefill(null)}
          onDone={() => {
            setPrefill(null);
            load();
          }}
          onError={setError}
        />
      )}

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      {schedules.length === 0 ? (
        <p className="text-sm text-slate-500">{t('adminPpm.empty')}</p>
      ) : (
        <ul className="divide-y divide-slate-800 rounded-xl border border-slate-800">
          {schedules.map((schedule) => (
            <li key={schedule.id} className="flex flex-wrap items-center gap-2 p-3">
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span
                    className={`truncate text-sm ${schedule.isActive ? 'text-slate-100' : 'text-slate-500'}`}
                  >
                    {schedule.title}
                  </span>
                  {!schedule.isActive && (
                    <RetiredBadge
                      retiredAt={schedule.retiredAt}
                      retiredReason={schedule.retiredReason}
                    />
                  )}
                </span>
                <span className="block truncate text-xs text-slate-500">
                  {t(intervalTypeLabelKey[schedule.intervalType as never])} ·{' '}
                  {t('adminPpm.nextDue', { date: formatDate(schedule.nextDueDate) })}
                  {schedule.location && ` · ${schedule.location.name}`}
                </span>
              </span>

              <button
                type="button"
                onClick={() => setPrefill(schedule)}
                className="inline-flex min-h-[40px] items-center gap-1 rounded bg-slate-800 px-3 text-xs text-slate-200"
              >
                <Copy className="size-3.5" aria-hidden="true" />
                {t('adminPpm.duplicate')}
              </button>

              {schedule.isActive && (
                <button
                  type="button"
                  onClick={() => setRetiring(schedule)}
                  className="min-h-[40px] rounded bg-transparent px-3 text-xs text-slate-500"
                >
                  {t('adminPpm.retire')}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {retiring && (
        <RetireDialog
          subject={retiring.title}
          onCancel={() => setRetiring(null)}
          onConfirm={async (reason) => {
            await adminApi.ppm.retire(retiring.id, reason);
            setRetiring(null);
            load();
          }}
        />
      )}
    </div>
  );
}

/**
 * Orders the picker's flat list parent-before-children and indents each name, so a
 * hundred places still read as a hierarchy inside one `<select>`.
 *
 * This replaces a recursive flatten of the admin tree, which arrived already nested. The
 * shape on the wire is now flat with a `parentId`, so the nesting is rebuilt here.
 *
 * Two deliberate defences. A place whose parent is absent from the response — retired, or
 * narrowed away by `?qrCode=` — surfaces at the top rather than being dropped, matching
 * what `LocationPicker` does with the same payload. And `seen` bounds a `parentId` cycle:
 * nothing should create one, but a maintenance screen is not where to discover that
 * something did.
 */
function indentPlaces(places: LocationOption[]): LocationOption[] {
  const byId = new Map(places.map((place) => [place.id, place]));
  const children = new Map<string | null, LocationOption[]>();

  for (const place of places) {
    const parent = place.parentId && byId.has(place.parentId) ? place.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), place]);
  }

  const seen = new Set<string>();
  const walk = (parentId: string | null, depth: number): LocationOption[] =>
    (children.get(parentId) ?? []).flatMap((place) => {
      if (seen.has(place.id)) return [];
      seen.add(place.id);

      return [
        { ...place, name: `${'— '.repeat(depth)}${place.name}` },
        ...walk(place.id, depth + 1),
      ];
    });

  return walk(null, 0);
}

/**
 * METER_BASED is omitted: it needs a threshold and a reading source, neither of which
 * belongs in a quick-create form.
 */
function IntervalSelect({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (next: string) => void;
  className: string;
}) {
  const { t } = useTranslation();

  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      aria-label={t('adminPpm.interval')}
      className={className}
    >
      {intervalTypeSchema.options
        .filter((option) => option !== 'METER_BASED')
        .map((option) => (
          <option key={option} value={option}>
            {t(intervalTypeLabelKey[option])}
          </option>
        ))}
    </select>
  );
}

function CreateScheduleForm({
  templates,
  locations,
  prefill,
  onClearPrefill,
  onDone,
  onError,
}: {
  templates: ChecklistTemplateOption[];
  locations: LocationOption[];
  prefill: AdminPpmSchedule | null;
  onClearPrefill: () => void;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const draft = useScheduleDraft(prefill, templates, locations);
  const { title, templateId, locationId, department, intervalType, nextDueDate } = draft;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || !templateId || !locationId) return;

    try {
      await adminApi.ppm.create({
        title: title.trim(),
        // Fields the quick-create form does not surface still ride along from the
        // predecessor, so a replacement is not silently simplified.
        description: prefill?.description ?? null,
        department,
        priority: (prefill?.priority ?? 'MEDIUM') as never,
        assetId: prefill?.assetId ?? null,
        locationId,
        checklistTemplateId: templateId,
        intervalType: intervalType as never,
        intervalValue: prefill?.intervalValue ?? 1,
        nextDueDate: new Date(nextDueDate),
      });
      draft.setTitle('');
      onDone();
    } catch (caught) {
      onError(caught instanceof Error ? caught.message : 'Failed');
    }
  }

  const fieldClass = 'rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm';

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-800 p-3">
      <FormHeading prefill={prefill} onClearPrefill={onClearPrefill} />
      <div className="flex flex-wrap gap-2">
        <input
          value={title}
          onChange={(event) => draft.setTitle(event.target.value)}
          placeholder={t('adminPpm.title')}
          aria-label={t('adminPpm.title')}
          className={`min-w-[160px] flex-1 ${fieldClass}`}
        />
        <select
          value={templateId}
          onChange={(event) => draft.setTemplateId(event.target.value)}
          aria-label={t('adminPpm.template')}
          className={fieldClass}
        >
          {templates.map((template) => (
            <option key={template.id} value={template.id}>
              {template.title} ({template._count.items})
            </option>
          ))}
        </select>
        <select
          value={locationId}
          onChange={(event) => draft.setLocationId(event.target.value)}
          aria-label={t('adminPpm.location')}
          className={fieldClass}
        >
          {locations.map((location) => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>
        <select
          value={department}
          onChange={(event) => draft.setDepartment(event.target.value as Department)}
          aria-label={t('adminPpm.department')}
          className={fieldClass}
        >
          {Object.values(Department).map((value) => (
            <option key={value} value={value}>
              {t(departmentLabelKey[value])}
            </option>
          ))}
        </select>
        <IntervalSelect
          value={intervalType}
          onChange={draft.setIntervalType}
          className={fieldClass}
        />
        <input
          type="date"
          value={nextDueDate}
          onChange={(event) => draft.setNextDueDate(event.target.value)}
          aria-label={t('adminPpm.firstDue')}
          className={fieldClass}
        />
        <button
          type="submit"
          disabled={!title.trim()}
          className="min-h-[44px] rounded bg-brookfields-green ring-1 ring-brookfields-gold/70 px-4 text-sm font-medium text-white disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
        >
          {t('adminPpm.add')}
        </button>
      </div>
    </form>
  );
}

/** Says plainly whether this form creates a schedule or replaces a retired one. */
function FormHeading({
  prefill,
  onClearPrefill,
}: {
  prefill: AdminPpmSchedule | null;
  onClearPrefill: () => void;
}) {
  const { t } = useTranslation();

  return (
    <p className="mb-2 flex items-center justify-between gap-2 text-xs text-slate-400">
      <span className="flex items-center gap-1.5">
        <CalendarClock className="size-3.5" aria-hidden="true" />
        {prefill ? t('adminPpm.replacing', { title: prefill.title }) : t('adminPpm.addHeading')}
      </span>
      {prefill && (
        <button
          type="button"
          onClick={onClearPrefill}
          className="min-h-0 bg-transparent p-0 text-xs text-slate-500 underline"
        >
          {t('adminPpm.clearPrefill')}
        </button>
      )}
    </p>
  );
}

/**
 * The draft fields for a new schedule, kept out of the form so the prefill resync
 * — the only fiddly part — reads on its own.
 */
function useScheduleDraft(
  prefill: AdminPpmSchedule | null,
  templates: ChecklistTemplateOption[],
  locations: LocationOption[],
) {
  const [title, setTitle] = useState('');
  const [templateId, setTemplateId] = useState(templates[0]?.id ?? '');
  const [locationId, setLocationId] = useState(locations[0]?.id ?? '');
  const [department, setDepartment] = useState<Department>(Department.Maintenance);
  const [intervalType, setIntervalType] = useState<string>('MONTHLY');
  const [nextDueDate, setNextDueDate] = useState(
    new Date(Date.now() + 86_400_000).toISOString().slice(0, 10),
  );

  /**
   * Carries what identifies the work, but never the due date: a successor that
   * inherited its predecessor's past due date would be swept into a ticket the
   * moment it was saved.
   */
  useEffect(() => {
    if (!prefill) return;
    setTitle(prefill.title);
    setTemplateId(prefill.checklistTemplateId);
    // Only if the place is still offered. The picker endpoint lists active places, where
    // the admin tree it replaced also carried retired ones — so duplicating a schedule
    // for a room since taken out of service would otherwise leave the select displaying
    // the first room while the draft submitted the retired one. Falling back to the
    // first option keeps what is shown and what is sent the same thing.
    setLocationId(
      locations.some((place) => place.id === prefill.locationId)
        ? prefill.locationId
        : (locations[0]?.id ?? ''),
    );
    setDepartment(prefill.department as Department);
    // IntervalSelect deliberately omits METER_BASED, so a meter-based predecessor
    // falls back to the default rather than selecting an option that is not there.
    if (prefill.intervalType !== 'METER_BASED') setIntervalType(prefill.intervalType);
    // `locations` is a dependency because the fallback above reads it: the duplicate
    // button can be pressed before the picker call has resolved.
  }, [prefill, locations]);

  return {
    title,
    setTitle,
    templateId,
    setTemplateId,
    locationId,
    setLocationId,
    department,
    setDepartment,
    intervalType,
    setIntervalType,
    nextDueDate,
    setNextDueDate,
  };
}
